import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import safeStorage from '../../utils/safeStorage';
import { 
  VpnKey as Key,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Warning as AlertCircle,
  Lock,
  LockOpen as Unlock,
  Security as Shield,
  Wifi,
  WifiOff
} from '@mui/icons-material';

const ProductKeyFromFacturation = ({ onLicenseValidated, onBack, onNext }) => {
  const [productKey, setProductKey] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // 🔑 LICENSING WORKER URL - MÊME URL QUE FACTURATION
  const LICENSING_API_URL = process.env.REACT_APP_LICENSING_API_URL || 'https://invoit-management.moundix-neuf19.workers.dev';

  // Monitor online status for kill switch functionality
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check if user already has a verified license on startup (Kill Switch Check)
  useEffect(() => {
    checkExistingLicense();
  }, []);


  // 🚨 KILL SWITCH: Check existing license on every app start
  const checkExistingLicense = async () => {
    const savedLicense = safeStorage.getItem('travelit_license'); // TravelIT specific key
    if (!savedLicense) return;

    try {
      const licenseData = JSON.parse(savedLicense);
      
      // Re-verify with server (Kill Switch functionality)
      if (isOnline) {
        const isStillValid = await verifyWithServer(licenseData.key);
        if (!isStillValid) {
          // License was revoked - clear it and show error
          safeStorage.removeItem('travelit_license');
          setError('Licence révoquée - contactez l\'administrateur');
        }
      }
    } catch (error) {
      console.error('License check error:', error);
      safeStorage.removeItem('travelit_license');
    }
  };

  const handleInputChange = (e) => {
    setProductKey(e.target.value.toUpperCase());
    if (error) setError('');
  };

  const formatProductKey = (key) => {
    // Format key with dashes every 5 characters (unlimited length)
    const cleaned = key.replace(/[^A-Z0-9]/g, '');
    let formatted = '';
    
    for (let i = 0; i < cleaned.length; i++) {
      if (i > 0 && i % 5 === 0) {
        formatted += '-';
      }
      formatted += cleaned[i];
    }
    
    return formatted;
  };

  const handleKeyInput = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    setProductKey(value); // Keep original formatting from paste/input
    if (error) setError('');
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData('text');
    const cleanPaste = paste.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    setProductKey(cleanPaste); // Keep exact format from clipboard
    if (error) setError('');
  };

  // 🌐 API CALL: Verify product key with Cloudflare Worker
  const verifyWithServer = async (key) => {
    try {
      const response = await fetch(`${LICENSING_API_URL}/verify?key=${encodeURIComponent(key)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      return {
        success: response.status === 200 && data.success,
        status: response.status,
        data: data
      };
      
    } catch (error) {
      console.error('License verification error:', error);
      return {
        success: false,
        status: 500,
        data: { error: 'Erreur de connexion au serveur' }
      };
    }
  };

  // 📝 VALIDATE PRODUCT KEY: Main validation function with real API
  const validateProductKey = async () => {
    if (!productKey.trim()) {
      setError('Veuillez entrer une clé de produit');
      return false;
    }

    if (!isOnline) {
      setError('Connexion Internet requise pour la vérification de licence');
      return false;
    }

    setIsValidating(true);
    setError('');

    try {
      const result = await verifyWithServer(productKey.trim());
      
      if (result.success) {
        // ✅ SUCCESS: Key is valid and activated
        const licenseData = {
          key: productKey.trim(),
          activatedAt: result.data.activatedAt || new Date().toISOString(),
          status: 'active',
          lastChecked: new Date().toISOString(),
          app: 'TravelIT' // Distinguish from INVOIT
        };

        // Store license securely with TravelIT specific key
        safeStorage.setItem('travelit_license', JSON.stringify(licenseData));
        
        console.log('✅ TravelIT License activated successfully:', productKey.substring(0, 8) + '...');
        return true;
        
      } else {
        // ❌ FAILED: Handle different error cases
        let errorMessage = 'Erreur de vérification de licence';
        
        switch (result.data.code) {
          case 'BANNED_KEY':
            errorMessage = 'Clé bannie - licence révoquée';
            break;
          case 'KEY_NOT_FOUND':
            errorMessage = 'Clé invalide ou déjà utilisée sur un autre appareil';
            break;
          case 'KEY_EXPIRED':
            errorMessage = 'Clé expirée';
            break;
          default:
            errorMessage = result.data.error || 'Clé de produit invalide';
        }
        
        setError(errorMessage);
        return false;
      }
      
    } catch (error) {
      console.error('Validation error:', error);
      setError('Erreur de connexion - vérifiez votre connexion Internet');
      return false;
      
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 🔑 REAL API VALIDATION: Connect to Cloudflare Worker Licensing System
    try {
      const isValid = await validateProductKey();
      
      if (isValid) {
        // License verified and stored - proceed to next step
        console.log('🎉 TravelIT License verification successful - proceeding to account creation');
        if (onLicenseValidated) {
          onLicenseValidated(); // 🚀 UTILISER LE CALLBACK SÉCURISÉ
        } else if (onNext) {
          onNext(); // Fallback pour TravelIT
        }
      }
      // If validation fails, error message is already set in validateProductKey()
      
    } catch (error) {
      console.error('Submit error:', error);
      setError('Erreur inattendue lors de la vérification');
      setIsValidating(false);
    }
  };
  
  const handleSkip = () => {
    // For development purposes only - would be removed in production
    if (onNext) onNext();
  };

  return (
    <div className="setup-card product-key">
      <div className="setup-header">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="welcome-logo"
        >
          <img 
            src="./images/logo.png" 
            alt="TravelIT Logo" 
            style={{ 
              width: '40px', 
              height: '40px', 
              objectFit: 'contain' 
            }} 
          />
        </motion.div>
        
        <h1 className="setup-title">Clé de Produit TravelIT</h1>
        <p className="setup-subtitle">
          Entrez votre clé de produit pour activer toutes les fonctionnalités
        </p>
        
        {/* Connection Status Indicator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          justifyContent: 'center',
          marginTop: '0.75rem',
          fontSize: '0.8rem',
          color: isOnline ? '#10b981' : '#ef4444'
        }}>
          {isOnline ? <Wifi sx={{ fontSize: 16 }} /> : <WifiOff sx={{ fontSize: 16 }} />}
          <span>{isOnline ? 'Connecté' : 'Hors ligne'}</span>
          {!isOnline && (
            <span style={{ color: 'rgba(255, 255, 255, 0.6)', marginLeft: '0.25rem' }}>
              - Connexion requise pour l'activation
            </span>
          )}
        </div>
        
        {/* License Information Panel */}
        <div style={{
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '12px',
          padding: '1rem',
          marginTop: '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.75rem',
            color: '#3b82f6'
          }}>
            <Shield sx={{ fontSize: 16 }} />
            <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Types de Licences Disponibles</span>
          </div>
          
          <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.4' }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: '#10b981' }}>TRAVEL-PREMIUM:</strong> Gestion complète voyages, réservations, rapports avancés
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: '#f59e0b' }}>TRAVEL-STANDARD:</strong> Fonctionnalités de base, gestion clients
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: '#8b5cf6' }}>TRAVEL-TRIAL:</strong> Version d'évaluation limitée dans le temps
            </div>
            <div>
              <strong style={{ color: '#6b7280' }}>TRAVEL-AGENCY:</strong> Accès API et fonctions agence complètes
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="setup-form">
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="error-message"
            style={{ 
              padding: '1rem', 
              backgroundColor: 'rgba(239, 68, 68, 0.1)', 
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem'
            }}
          >
            <AlertCircle sx={{ fontSize: 16 }} />
            {error}
          </motion.div>
        )}

        <div className="product-key-container" style={{ marginBottom: '2rem' }}>
          <label 
            htmlFor="productKey" 
            className="form-label"
            style={{ marginBottom: '0.75rem', display: 'block' }}
          >
            Clé de produit TravelIT
            <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
              (Format: TRAVEL-XXXXX-XXXXX-XXXXX-XXXXX)
            </span>
          </label>
          
          <div style={{ 
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <input
              type="text"
              id="productKey"
              value={productKey}
              onChange={handleKeyInput}
              onPaste={handlePaste}
              placeholder="TRAVEL-XXXXX-XXXXX-XXXXX-XXXXX"
              className="form-input"
              style={{ 
                paddingLeft: '3rem', 
                letterSpacing: '1px', 
                fontFamily: 'monospace',
                fontSize: '1.1rem',
                paddingTop: '0.75rem',
                paddingBottom: '0.75rem'
              }}
              maxLength={35}
            />
            <div style={{ 
              position: 'absolute', 
              left: '1rem', 
              pointerEvents: 'none',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              <Key sx={{ fontSize: 20 }} />
            </div>
          </div>

          <div style={{ 
            marginTop: '1rem',
            display: 'flex',
            alignItems: 'center',
            color: productKey ? '#10b981' : 'rgba(255, 255, 255, 0.5)',
            gap: '0.5rem',
            fontSize: '0.875rem'
          }}>
            {productKey ? <Unlock sx={{ fontSize: 16 }} /> : <Lock sx={{ fontSize: 16 }} />}
            {productKey 
              ? "Clé prête à être validée" 
              : "Entrez votre clé de produit pour continuer"}
          </div>
        </div>

        <div style={{ 
          padding: '1.5rem', 
          backgroundColor: 'rgba(59, 130, 246, 0.1)', 
          borderRadius: '10px',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          marginBottom: '2rem'
        }}>
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            alignItems: 'flex-start',
            marginBottom: '0.75rem'
          }}>
            <Shield sx={{ fontSize: 20, color: '#3b82f6', flexShrink: 0, marginTop: '0.2rem' }} />
            <div>
              <h3 style={{ 
                margin: '0 0 0.5rem', 
                fontSize: '1rem', 
                fontWeight: '600', 
                color: 'white' 
              }}>
                Pourquoi une clé de produit ?
              </h3>
              <p style={{ 
                margin: '0',
                fontSize: '0.875rem',
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: '1.5'
              }}>
                Votre clé de produit déverrouille toutes les fonctionnalités premium de TravelIT.
                Elle garantit également des mises à jour et un support technique pendant toute la durée de votre licence.
              </p>
            </div>
          </div>
        </div>

        <div className="setup-actions">
          <motion.button
            type="button"
            className="cta-button-compact btn-secondary-setup"
            onClick={onBack}
            disabled={isValidating}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <ChevronLeft sx={{ fontSize: 18 }} />
            <span>Retour</span>
          </motion.button>
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <motion.button
              type="submit"
              className="cta-button-compact"
              disabled={isValidating}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              {isValidating ? (
                <>
                  <div className="loading-spinner" />
                  <span>Validation...</span>
                </>
              ) : (
                <>
                  <span>Activer</span>
                  <ChevronRight sx={{ fontSize: 18 }} />
                </>
              )}
            </motion.button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductKeyFromFacturation;
